/**
 * Detailed-capability taxonomy, grouped by logical subcategory.
 *
 * Single source of truth for the capability options offered on **both** intakes — the CDMO
 * Primary Capabilities step and the Sponsor matching sidebar. `DETAILED_CAPABILITIES_OPTIONS_MAP`
 * in `./index` is derived from this file by flattening, so the two sides cannot drift and the
 * existing Tier 1 gate keeps comparing the same canonical strings.
 *
 * Two rules govern edits here:
 *
 * 1. **The label is the stored value.** There are no capability IDs — `cdmo_applications.
 *    detailed_capabilities` holds these exact strings, and {@link ../lib/gatekeeperFilters}
 *    compares them exactly. Renaming a label without a data migration orphans every CDMO that
 *    selected it. Add renames to {@link LEGACY_DETAILED_CAPABILITY_ALIASES} as well.
 * 2. **"General capabilities" comes first, in its original order.** Flattening therefore yields
 *    the pre-expansion array with the new options appended, so stored selections and any
 *    order-sensitive consumer keep working.
 *
 * Modality-specific suffixes ("— Small Molecule", "— Biologics") are intentional: the same
 * concept in two modalities must be two distinct backend values so a Biologics-only
 * lyophilization capability never satisfies a Small Molecule requirement.
 */

import { withAsciiDashes } from '../utils/labelMatching';

/**
 * Own-property lookup guard for the modality-keyed records in this module.
 *
 * Modality keys reach these lookups from stored profiles and saved sponsor searches, so they are
 * untrusted strings. A bare index on a plain object resolves inherited `Object.prototype` members —
 * `groups['constructor']` hands back the `Object` function, which is not nullish, so a `?? []`
 * fallback never fires and a function flows on into code expecting an array.
 */
export function hasOwn(record: object, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(record, key);
}

/** One subcategory heading and the capability options filed under it. */
export interface CapabilityGroup {
  /** Display heading, e.g. "Sterile / injectable". Never stored — UI grouping only. */
  group: string;
  /** Canonical capability labels. These strings are the stored values. */
  options: string[];
}

/** Heading for the broad, pre-expansion options that stay visible in every modality. */
export const GENERAL_CAPABILITY_GROUP = 'General capabilities';

/**
 * Modality → subcategory groups. Group headings for Small Molecule, Cell & Gene Therapy,
 * Vaccines and Medical Devices are taken verbatim from the development scope; Biologics has no
 * scope-supplied headings, so its additions are grouped by process stage.
 */
export const DETAILED_CAPABILITY_GROUPS: Record<string, CapabilityGroup[]> = {
  'Small Molecule': [
    {
      group: GENERAL_CAPABILITY_GROUP,
      options: [
        'API Manufacturing',
        'Drug Product Formulation',
        'Oral Solid Dosage',
        'Parenteral (Injectable)',
        'Topical/Dermal',
        'Inhalation Products',
        'Modified Release',
        'Controlled Substances',
        'High Potency APIs (HPAPIs)',
        'Analytical Method Development',
        'Stability Studies',
        'Process Development & Optimization',
      ],
    },
    {
      group: 'Oral solid dose',
      options: [
        'Tablet Manufacturing',
        'Capsule Manufacturing',
        'Softgel Manufacturing',
        'Wet Granulation',
        'Dry Granulation / Roller Compaction',
        'Direct Compression / Dry Blending',
        'Spray Drying',
        'Hot-Melt Extrusion',
        'Milling / Micronization / Particle Size Reduction',
        'Tablet & Multiparticulate Coating',
      ],
    },
    {
      group: 'Oral liquid',
      options: ['Oral Liquid Dosage — Solutions & Suspensions'],
    },
    {
      group: 'Sterile / injectable',
      options: [
        'Lyophilization — Small Molecule',
        'Sterile Fill/Finish — Small Molecule',
        'Vial Filling — Small Molecule',
        'Prefilled Syringe Filling — Small Molecule',
        'Cartridge Filling — Small Molecule',
      ],
    },
    {
      group: 'Inhalation & nasal',
      options: [
        'pMDI (Pressurized Metered-Dose Inhaler) — Small Molecule',
        'DPI (Dry Powder Inhaler) — Small Molecule',
        'Nebulized Inhalation Product — Small Molecule',
        'Soft-Mist Inhalation Product — Small Molecule',
        'Nasal Spray / Nasal Drug Product — Small Molecule',
      ],
    },
    {
      group: 'Topical / semi-solid',
      options: ['Cream Manufacturing', 'Ointment Manufacturing', 'Gel Manufacturing'],
    },
    {
      group: 'Other dosage form',
      options: ['Ophthalmic Drug Product — Small Molecule'],
    },
  ],

  'Biologics': [
    {
      group: GENERAL_CAPABILITY_GROUP,
      options: [
        'Monoclonal Antibodies',
        'Recombinant Proteins',
        'Biosimilars',
        'Antibody-Drug Conjugates (ADCs)',
        'Fusion Proteins',
        'Cytokines & Growth Factors',
        'Mammalian Cell Culture',
        'Microbial Fermentation',
        'Purification & Downstream Processing',
        'Formulation Development',
        // Renamed in place — keeps the pre-expansion ordering intact.
        'Lyophilization — Biologics',
        'Sterile Fill/Finish — Biologics',
        'Analytical Characterization',
        'Bioassays & Potency Testing',
      ],
    },
    {
      group: 'Molecule type',
      options: ['Bispecific / Multispecific Antibodies', 'Therapeutic Enzymes'],
    },
    {
      group: 'Upstream & bioprocess',
      options: [
        'Cell Line Development — Biologics',
        'Bioconjugation — Biologics',
        'Perfusion Cell Culture — Biologics',
      ],
    },
    {
      group: 'Formulation & fill',
      options: [
        'High-Concentration Biologic Formulation',
        'Vial Filling — Biologics',
        'Prefilled Syringe Filling — Biologics',
        'Cartridge Filling — Biologics',
      ],
    },
    {
      group: 'Inhalation & nasal',
      options: ['Inhaled Biologic Drug Product', 'Nasal Biologic Drug Product'],
    },
  ],

  'Cell & Gene Therapy': [
    {
      group: GENERAL_CAPABILITY_GROUP,
      options: [
        'Viral Vector Manufacturing',
        'CAR-T Cell Processing',
        'Stem Cell Processing',
        'Gene Editing (CRISPR)',
        'Plasmid DNA Manufacturing',
        'Lentiviral Vectors',
        'Adenoviral Vectors',
        'AAV Vectors',
        'Cell Banking',
        'Quality Control Testing',
        'Cryopreservation',
        // Renamed in place.
        'Chain of Identity & Chain of Custody Management',
      ],
    },
    {
      group: 'Cell therapy type',
      options: [
        'Autologous Cell Therapy Manufacturing',
        'Allogeneic Cell Therapy Manufacturing',
        'TCR-T Cell Therapy',
        'NK Cell Therapy',
        'TIL (Tumor-Infiltrating Lymphocyte) Therapy',
        'iPSC-Based Cell Therapy',
        'Mesenchymal Stem/Stromal Cell (MSC) Therapy Manufacturing',
        'Hematopoietic Stem Cell (HSC) Processing',
      ],
    },
    {
      group: 'Gene/vector manufacturing',
      options: ['Non-Viral Gene Delivery / Electroporation', 'Viral Vector Fill/Finish'],
    },
    {
      group: 'Final product / testing',
      options: [
        'Cell Therapy Formulation & Fill/Finish',
        'Potency / Functional Assays — Cell & Gene Therapy',
        'Sterility Testing — Cell & Gene Therapy',
      ],
    },
  ],

  'Vaccines': [
    {
      group: GENERAL_CAPABILITY_GROUP,
      options: [
        'Live Attenuated Vaccines',
        'Inactivated Vaccines',
        'Subunit Vaccines',
        'mRNA Vaccines',
        'Viral Vector Vaccines',
        'Adjuvant Formulation',
        // Renamed in place.
        'Multi-Dose Vial Fill — Vaccines',
        'Prefilled Syringe Fill — Vaccines',
        'Cold Chain Management',
        'Stability Testing',
        'Potency Assays',
        'Sterility Testing',
      ],
    },
    {
      group: 'Vaccine platform',
      options: [
        'Conjugate Vaccine Manufacturing',
        'Virus-Like Particle (VLP) Vaccine Manufacturing',
        'DNA Vaccine Manufacturing',
        'Toxoid Vaccine Manufacturing',
        'Recombinant Protein Vaccine Manufacturing',
      ],
    },
    {
      group: 'mRNA-specific',
      options: ['LNP Formulation — mRNA Vaccines'],
    },
    {
      group: 'Drug product',
      options: [
        'Lyophilization — Vaccines',
        'Sterile Fill/Finish — Vaccines',
        'Single-Dose Vial Fill — Vaccines',
        'Intranasal / Nasal Vaccine Manufacturing',
      ],
    },
  ],

  // Modality name stays "Medical Devices" by product decision. Combination Products and
  // Drug-Device Combinations remain separate selectable capabilities.
  'Medical Devices': [
    {
      group: GENERAL_CAPABILITY_GROUP,
      options: [
        'Combination Products',
        'Drug-Device Combinations',
        // Renamed in place.
        'Prefilled Syringes',
        'Autoinjectors',
        'Inhalation Delivery Devices',
        'Transdermal Patches',
        'Implantable Devices',
        'Diagnostic Kits',
        'Standalone Contract Sterilization Services',
        'Packaging Validation',
        'Biocompatibility Testing',
      ],
    },
    {
      group: 'Injectable delivery systems',
      options: [
        'Pen Injectors',
        'Cartridge-Based Delivery Systems',
        'On-Body / Wearable Injectors',
        'Needle-Free Injectors',
      ],
    },
    {
      group: 'Inhalation devices',
      options: ['pMDI Devices', 'DPI Devices', 'Soft-Mist Inhaler Devices', 'Nebulizer Systems'],
    },
    {
      group: 'Nasal',
      options: ['Nasal Spray Delivery Devices'],
    },
    {
      group: 'Other',
      options: [
        'Infusion Pump Systems',
        'Device Assembly',
        'Combination-Product Assembly',
        'Design Verification & Validation',
      ],
    },
  ],
};

/**
 * Pre-expansion label → current canonical label, for the capabilities renamed by the taxonomy
 * expansion.
 *
 * A data migration rewrites these in `cdmo_applications.detailed_capabilities` and
 * `introduction_requests.request_details`, but this map is the safety net: a row the migration
 * missed (inserted mid-deploy, restored from a backup) still resolves to the right capability
 * instead of silently dropping out of every search. Consumed through {@link
 * resolveDetailedCapabilityLabel} below, including by `normalizeDetailedCapabilityLabel` in
 * `../lib/gatekeeperFilters`.
 *
 * Every key is unique across the entire pre-expansion taxonomy, so the mapping is unambiguous
 * even where the stored value carries no modality context.
 */
export const LEGACY_DETAILED_CAPABILITY_ALIASES: Record<string, string> = {
  // Biologics
  'Lyophilization': 'Lyophilization — Biologics',
  'Sterile Fill/Finish': 'Sterile Fill/Finish — Biologics',
  // Cell & Gene Therapy
  'Chain of Custody Management': 'Chain of Identity & Chain of Custody Management',
  // Vaccines
  'Multi-dose Vial Fill': 'Multi-Dose Vial Fill — Vaccines',
  'Pre-filled Syringe Fill': 'Prefilled Syringe Fill — Vaccines',
  // Medical Devices
  'Pre-filled Syringes': 'Prefilled Syringes',
  'Auto-injectors': 'Autoinjectors',
  'Inhalation Devices': 'Inhalation Delivery Devices',
  'Sterilization Services': 'Standalone Contract Sterilization Services',
  'Package Validation': 'Packaging Validation',
};

/** Case/whitespace/dash folding — the comparison key used to match a label against an alias tolerantly. */
function foldDetailedCapabilityLabel(value: string): string {
  return withAsciiDashes(value.trim().toLowerCase()).replace(/\s+/g, ' ');
}

/**
 * Folded alias key → canonical display label. Built once at module load so a variant-cased or
 * differently-spaced legacy label still resolves to the exact canonical string.
 */
const FOLDED_LEGACY_DETAILED_CAPABILITY_ALIASES: ReadonlyMap<string, string> = new Map(
  Object.entries(LEGACY_DETAILED_CAPABILITY_ALIASES).map(([legacy, current]) => [
    foldDetailedCapabilityLabel(legacy),
    current,
  ])
);

/**
 * Resolves a stored detailed-capability label to its current canonical display text.
 *
 * Tolerant of case, whitespace and typographic-dash variants of a legacy label — the alias
 * table's own docblock notes rows can arrive "restored from a backup", which is exactly where
 * variant casing comes from. Returns the input unchanged when it does not match a legacy key
 * (already canonical, or unrecognized).
 *
 * Single source of truth for this resolution: `CDMOProfile.tsx` (profile load, before display),
 * `sponsorCardDetails.ts` (the sponsor-facing match card) and `gatekeeperFilters.ts` (folded
 * further into a comparison key) all resolve through this function rather than each keeping its
 * own alias-lookup rules.
 */
export function resolveDetailedCapabilityLabel(value: string): string {
  return FOLDED_LEGACY_DETAILED_CAPABILITY_ALIASES.get(foldDetailedCapabilityLabel(value)) ?? value;
}

/**
 * Resolves and de-duplicates a list of stored detailed-capability labels, preserving first-seen
 * order. A non-array input yields `[]` rather than throwing, since these values come from stored
 * JSON that predates the current shape.
 */
export function canonicalizeDetailedCapabilityLabels(
  labels: readonly string[] | undefined | null
): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const label of Array.isArray(labels) ? labels : []) {
    const canonical = resolveDetailedCapabilityLabel(label);
    if (!seen.has(canonical)) {
      seen.add(canonical);
      result.push(canonical);
    }
  }
  return result;
}

/**
 * Flattens grouped capabilities into the plain option list the matching engine and stored
 * profiles use.
 *
 * @param groups - Subcategory groups for one modality
 * @returns Every option, in group order
 */
export function flattenCapabilityGroups(groups: readonly CapabilityGroup[]): string[] {
  return groups.flatMap((entry) => entry.options);
}

/**
 * Subcategory groups for a modality.
 *
 * @param modality - Primary capability key, e.g. "Small Molecule"
 * @returns The modality's groups, or an empty array when the modality is unknown
 */
export function getCapabilityGroups(modality: string): CapabilityGroup[] {
  return hasOwn(DETAILED_CAPABILITY_GROUPS, modality) ? DETAILED_CAPABILITY_GROUPS[modality] : [];
}

/**
 * Arranges an arbitrary option list into this modality's subcategory groups.
 *
 * Used by both pickers so they render identically. Options with no known group — a modality
 * absent from {@link DETAILED_CAPABILITY_GROUPS}, or a stored value predating the current
 * taxonomy — fall back to a single unlabelled group so nothing is ever hidden from the user.
 *
 * @param modality - Primary capability key
 * @param options - Options to arrange; defaults to the modality's full option list
 * @returns Non-empty groups in taxonomy order, with any ungrouped options appended
 */
export function groupCapabilityOptions(
  modality: string,
  options?: readonly string[]
): CapabilityGroup[] {
  const groups = getCapabilityGroups(modality);
  if (options === undefined) return groups;

  const remaining = new Set(options);
  const grouped: CapabilityGroup[] = [];

  for (const entry of groups) {
    const present = entry.options.filter((option) => remaining.has(option));
    present.forEach((option) => remaining.delete(option));
    if (present.length > 0) grouped.push({ group: entry.group, options: present });
  }

  if (remaining.size > 0) {
    grouped.push({ group: '', options: options.filter((option) => remaining.has(option)) });
  }

  return grouped;
}

/**
 * Narrows grouped capabilities to those matching a search query.
 *
 * Backs the "Search capabilities" field both intakes require. Matching is case-insensitive
 * substring on the option label; groups left with nothing are dropped so only relevant headings
 * render. An empty query returns the groups untouched.
 *
 * @param groups - Groups to filter
 * @param query - Raw search text
 * @returns Groups containing at least one match
 */
export function filterCapabilityGroups(
  groups: readonly CapabilityGroup[],
  query: string
): CapabilityGroup[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return groups.map((entry) => ({ ...entry, options: [...entry.options] }));

  return groups
    .map((entry) => ({
      group: entry.group,
      options: entry.options.filter((option) => option.toLowerCase().includes(needle)),
    }))
    .filter((entry) => entry.options.length > 0);
}

/** Total number of options across groups. */
export function countCapabilityOptions(groups: readonly CapabilityGroup[]): number {
  return groups.reduce((total, entry) => total + entry.options.length, 0);
}
